import React, {useEffect, useRef, useState} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    FormControlLabel,
    Switch,
    Typography,
    Paper,
    Alert,
    DialogTitle,
    DialogContent,
    Stack,
} from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';

import {IShowKKMData} from './type/IKKMData';
import instance from '../../../instance';
import {ICreateKKMRequest, IDeleteKKMRequest, IEditKKMRequest} from '../../../interfaces/fetch/kkm';
import {IDepartmenstData} from './type/IDepartmenstData';
import moment from 'moment-timezone';
import GetConstants from '../../../context/ConstantsContext';
import CallNotification from '../../../context/NotificationContext';
import HelpPopover from '../../../ui/HelpPopover';
import {EServiceRent} from '../../../Enums/ServiceRent';
import {IServiceDisconnect} from '../../../interfaces/KKM/IServiceDisconnect';
import {EDisconnectReason} from '../../../Enums/DisconnectReason';
import {formatDate} from '../../../functionts/formatDate';

interface IOpenPopupProps {
    deskOpen: boolean;
    setOpenDesk: React.Dispatch<React.SetStateAction<boolean>>;
    isCreate: boolean;
    activeRow: IShowKKMData | null;
    userId: number | null;
    serviceCenterId: number | null;
    isLoading: boolean;
    onKkmUpdate?: (userId: number, updatedKkm: any) => void;
    onKkmCreate?: (userId: number, newKkm: any) => void;
    onKkmDelete?: (userId: number, deletedKkmId: number) => void;
}

interface IErrorState {
    deskName: string;
    deskModel: string;
    serialNumber: string;
    regNumber: string;
    ofd: string;
}

const KKMPopup = (props: IOpenPopupProps): React.JSX.Element => {
    const {deskOpen, setOpenDesk, isCreate, activeRow, userId, serviceCenterId, isLoading, onKkmUpdate, onKkmCreate, onKkmDelete} = props;
    const {handleErrorNotification} = CallNotification();
    const {OFD, KKMType} = GetConstants();
    const REG_NUMBER_LEN = 12;
    const ERROR_DEFAULT: IErrorState = {
        deskName: '',
        deskModel: '',
        serialNumber: '',
        regNumber: '',
        ofd: '',
    };

    const [errorState, setErrorState] = useState<IErrorState>(ERROR_DEFAULT);
    const [departments, setDepartments] = useState<IDepartmenstData[]>([]);
    const [isLoadingDepartments, setIsLoadingDepartments] = useState<boolean>(false);
    const [nomenclature, setNomenclature] = useState<boolean>(true);
    const [premiumOFD, setPremiumOFD] = useState<boolean>(false);
    const [currentOfdId, setCurrentOfdId] = useState<number>(0);
    const [disabledByClient, setDisabledByClient] = useState<boolean>(false);
    const [disabledByClientOnInit, setDisabledByClientOnInit] = useState<boolean | undefined>(undefined);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const regNumberInputRef = useRef<HTMLInputElement | null>(null);
    const serialNumberInputRef = useRef<HTMLInputElement | null>(null);
    const ofdInputRef = useRef<HTMLInputElement | null>(null);
    const departmentInputRef = useRef<HTMLInputElement | null>(null);
    const kkmTypeInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (deskOpen && (isCreate || activeRow?.ofd?.id)) {
            const initValue = isCreate ? 0 : Number(activeRow?.ofd?.id);
            setCurrentOfdId(initValue);
        }
    }, [deskOpen, isCreate, activeRow]);

    // 1) Инициализация флага при редактировании
    useEffect(() => {
        if (!deskOpen || isCreate || !activeRow) return;
        const hasNomenclature = activeRow.service_rent?.some(r => r.service_rent_id === EServiceRent.Nomenclature);
        const hasPremium = activeRow.service_rent?.some(r => r.service_rent_id === EServiceRent.PremiumOFD);
        setNomenclature(Boolean(hasNomenclature));
        setPremiumOFD(Boolean(hasPremium));
    }, [deskOpen, isCreate, activeRow]);

    useEffect(() => {
        if (deskOpen && activeRow) {
            const isDisabledByClient = activeRow.service_disconnect?.some(
                (item: IServiceDisconnect) => item.disconnect_reason_id === EDisconnectReason.DisabledByClient,
            );

            setDisabledByClient(Boolean(isDisabledByClient));
            setDisabledByClientOnInit(Boolean(isDisabledByClient));
        }
    }, [deskOpen, activeRow]);

    useEffect(() => {
        if (isCreate) {
            setPremiumOFD(false);
        }
    }, [isCreate]);

    useEffect(() => {
        if (!userId) return;

        const fetchDepartments = async () => {
            setIsLoadingDepartments(true);

            try {
                const request = {
                    method: 'GetDepartment',
                    params: {user_id: userId},
                };
                const response = await instance.post('/LK', request);
                handleErrorNotification(response);
                setDepartments(response.data?.department || []);
            } catch (error) {
                console.error('Ошибка при получении мест торговли', error);
            } finally {
                setIsLoadingDepartments(false);
            }
        };

        void fetchDepartments();
    }, [userId, deskOpen]);

    const validationData = (
        isCreateForm: boolean = true,
        deskModel: string | 0,
        serialNumber: string | null,
        regNumber: string,
        ofd: string,
    ) => {
        let isValid = true;
        const error: IErrorState = ERROR_DEFAULT;

        if (!deskModel || Number(deskModel) === 0) {
            isValid = false;
            error.deskModel = 'Выберите модель кассы';
        }

        if (isCreateForm && !serialNumber?.trim()) {
            isValid = false;
            error.serialNumber = 'Это поле обязательно для заполнения';
        }

        if (regNumber.length != REG_NUMBER_LEN) {
            isValid = false;
            error.regNumber = 'Номер должен состоять из ' + REG_NUMBER_LEN + ' цифр';
        }

        if (!ofd || ofd === '0') {
            isValid = false;
            error.ofd = 'Это поле обязательно для заполнения';
        }

        setErrorState(error);

        return isValid;
    };

    const handlerClose = () => {
        setOpenDesk(false);
        setErrorState(ERROR_DEFAULT);
    };

    const handlerAdd = async () => {
        if (
            !kkmTypeInputRef.current ||
            !regNumberInputRef.current ||
            !serialNumberInputRef.current ||
            !ofdInputRef.current ||
            !departmentInputRef.current
        ) {
            return;
        }

        const isValid = validationData(
            true,
            kkmTypeInputRef.current.value,
            serialNumberInputRef.current.value.trim(),
            regNumberInputRef.current.value.trim(),
            ofdInputRef.current.value,
        );

        const date = new Date();
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const dateTime = moment.tz(date, tz).format();

        if (isValid) {
            const request: ICreateKKMRequest = {
                method: 'CreateKKMItem',
                params: {
                    user_id: userId ? userId : -1,
                    service_center_id: serviceCenterId,
                    model: KKMType && KKMType[Number(kkmTypeInputRef.current.value)]?.name,
                    reg_number: regNumberInputRef.current.value.trim(),
                    serial_number: serialNumberInputRef.current.value.trim(),
                    ofd: Number(ofdInputRef.current.value),
                    date: String(dateTime),
                    department_id: Number(departmentInputRef.current.value) || undefined,
                    nomenclature: nomenclature,
                    premium_ofd: premiumOFD,
                },
            };

            setIsSaving(true);

            try {
                const response = await instance.post('/LK', request);
                handleErrorNotification(response, true);

                if (response.data.kkm_id) {
                    const itemResponse = await instance.post('/LK', {
                        method: 'GetKKMItem',
                        params: {
                            user_id: userId,
                            kkm_id: response.data.kkm_id,
                        },
                    });

                    handleErrorNotification(itemResponse);

                    if (itemResponse.data?.kkm_item) {
                        onKkmCreate?.(userId!, itemResponse.data.kkm_item);
                    }
                }
            } catch (error) {
                console.log('Some Error with authorization request: ' + (error as Error).message);
            } finally {
                setIsSaving(false);
                handlerClose();
            }
        }
    };

    const handlerRemove = async () => {
        const confirm = window.confirm('Вы уверены, что хотите удалить кассу?');
        if (!confirm) return;

        const request: IDeleteKKMRequest = {
            method: 'DeleteKKMItem',
            params: {
                kkm_id: activeRow?.kkm_id,
                user_id: userId,
                service_center_id: serviceCenterId,
            },
        };

        setIsSaving(true);

        try {
            const response = await instance.post('/LK', request);
            handleErrorNotification(response, true);

            if (activeRow?.kkm_id) {
                onKkmDelete?.(userId!, activeRow.kkm_id);
            }
        } catch (error) {
            console.log('Some Error with authorization request: ' + (error as Error).message);
        } finally {
            setIsSaving(false);
            handlerClose();
        }
    };

    const handlerUpdate = async () => {
        if (!kkmTypeInputRef.current || !regNumberInputRef.current || !ofdInputRef.current || !departmentInputRef.current) {
            return;
        }

        const isValid = validationData(
            false,
            kkmTypeInputRef.current.value,
            null,
            regNumberInputRef.current.value.trim(),
            ofdInputRef.current.value,
        );

        if (!isValid) return;

        let requestParam: any = {
            kkm_id: activeRow?.kkm_id,
            user_id: userId,
            service_center_id: serviceCenterId,
            nomenclature: nomenclature,
            premium_ofd: premiumOFD,
            ofd: Number(ofdInputRef.current.value),
            model: KKMType && KKMType[Number(kkmTypeInputRef.current.value)].name,
            reg_number: regNumberInputRef.current.value,
            disabled_by_client: disabledByClient !== disabledByClientOnInit ? disabledByClient : null,
        };

        if (Number(departmentInputRef.current.value) !== -1) {
            requestParam = {
                ...requestParam,
                department_id: Number(departmentInputRef.current.value),
            };
        }

        const request: IEditKKMRequest = {
            method: 'EditKKMItem',
            params: {
                ...requestParam,
            },
        };

        setIsSaving(true);

        try {
            const response = await instance.post('/LK', request);
            handleErrorNotification(response, true);

            if (response.data) {
                const itemResponse = await instance.post('/LK', {
                    method: 'GetKKMItem',
                    params: {
                        user_id: userId,
                        kkm_id: activeRow?.kkm_id,
                    },
                });
                handleErrorNotification(itemResponse);

                if (itemResponse.data?.kkm_item) {
                    onKkmUpdate?.(userId!, itemResponse.data.kkm_item);
                }
            }
        } catch (error) {
            console.log('Some Error with authorization request: ' + (error as Error).message);
        } finally {
            setIsSaving(false);
            handlerClose();
        }
    };

    const hasOnlyPremiumOFD = (() => {
        const rents = activeRow?.service_rent || [];
        return rents.length === 1 && rents[0].service_rent_id === EServiceRent.PremiumOFD;
    })();

    const renderProxySwitch = () => (
        <Box mb={1}>
            <FormControlLabel
                control={<Switch checked disabled />}
                label={
                    <Box display="flex" alignItems="center">
                        «Прокси»
                        <HelpPopover
                            content={
                                <>
                                    Стандартный тариф у каждой кассы, отключить нельзя. Связь кассы с ПОРТall и передача чеков в любой ОФД.
                                    <br />
                                    <br />
                                    1015 ₸/мес с НДС.
                                </>
                            }
                        />
                    </Box>
                }
            />
        </Box>
    );

    const renderRentInfoOrSwitches = () => {
        if (!activeRow) return null;

        return (
            <>
                <Paper variant="outlined" sx={{p: 2, mt: 2, backgroundColor: '#f9f9f9'}}>
                    <Typography variant="subtitle2" gutterBottom>
                        Подключаемые тарифы
                    </Typography>

                    {renderProxySwitch()}

                    <Box mb={1}>
                        <FormControlLabel
                            control={<Switch checked={nomenclature} onChange={() => setNomenclature(prev => !prev)} />}
                            label={
                                <Box display="flex" alignItems="center">
                                    «Номенклатура»
                                    <HelpPopover
                                        content={
                                            <>
                                                Работа с национальным каталогом товаров и личным кабинетом номенклатуры.
                                                <br />
                                                <br />
                                                Дополнительно 1015 ₸/мес с НДС к тарифу «Прокси».
                                            </>
                                        }
                                    />
                                </Box>
                            }
                        />
                    </Box>
                </Paper>
            </>
        );
    };

    const renderRentDetails = () => {
        if (!activeRow) return null;

        const rentBase = activeRow.service_rent?.find(r => r.service_rent_id === EServiceRent.Base);
        const gasStation = activeRow.service_sign?.find(s => s.service_sign_id === 1);
        const hasInitialNomenclature = activeRow.service_rent?.some(r => r.service_rent_id === EServiceRent.Nomenclature) || false;

        const rows: string[] = [];

        const finishDate = formatDate(rentBase?.finish_date);

        if (finishDate) {
            if (gasStation && rentBase && !nomenclature) {
                rows.push(`«АЗС» — Необходимо перейти на другой тариф до ${finishDate}`);
            } else if (gasStation && rentBase) {
                rows.push(`«АЗС» — Действует до ${finishDate}`);
            } else if (rentBase && !nomenclature) {
                rows.push(`«Прокси» — Действует до ${finishDate}`);
            }
        }

        if (!activeRow.service_rent?.length && !nomenclature) {
            return (
                <Alert severity="warning" sx={{mt: 2}}>
                    Внимание: для кассы не активен ни один тариф.
                    <br />
                    Работа устройства может быть некорректной или полностью остановлена.
                    <br />
                    Чтобы привязать тариф, необходимо удалить кассу и создать её заново с нужными параметрами.
                </Alert>
            );
        }

        return (
            <Paper variant="outlined" sx={{p: 2, mt: 2, backgroundColor: '#f9f9f9'}}>
                <Typography variant="subtitle2" gutterBottom>
                    Подключённые тарифы
                </Typography>
                {renderProxySwitch()}
                <Box mb={1}>
                    <FormControlLabel
                        control={
                            <Switch checked={nomenclature} onChange={() => setNomenclature(prev => !prev)} disabled={hasInitialNomenclature} />
                        }
                        label={
                            <Box display="flex" alignItems="center">
                                «Номенклатура»
                                <HelpPopover
                                    content={
                                        <>
                                            Работа с национальным каталогом товаров и личным кабинетом номенклатуры.
                                            <br />
                                            <br />
                                            Дополнительно 1015 ₸/мес с НДС к тарифу «Прокси».
                                        </>
                                    }
                                />
                            </Box>
                        }
                    />
                </Box>
                {rows.map((row, index) => (
                    <Alert key={index} severity="warning" sx={{my: 1, pl: 0, pr: 0}}>
                        {row}
                    </Alert>
                ))}
                {hasInitialNomenclature ? (
                    <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                        Чтобы отключить тариф «Номенклатура», необходимо удалить кассу и создать её заново с нужными параметрами.
                    </Typography>
                ) : (
                    <>
                        {nomenclature ? (
                            <Typography variant="caption" color="error.main" display="block" mt={1}>
                                Рекомендуем оплатить заранее, чтобы избежать блокировки кассы.
                            </Typography>
                        ) : (
                            <></>
                        )}
                        <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                            Изменения вступят в силу после сохранения.
                        </Typography>
                    </>
                )}
            </Paper>
        );
    };

    return (
        <Dialog open={deskOpen} onClose={handlerClose} fullWidth maxWidth="sm">
            <Box
                sx={{
                    p: '20px 24px 10px 3px',
                    position: 'relative',
                    backgroundColor: '#f5f7ff',
                }}
            >
                <DialogTitle sx={{pr: 5}}>{isCreate ? 'Добавить кассу' : 'Редактировать кассу'}</DialogTitle>
                <IconButton
                    onClick={handlerClose}
                    sx={{
                        position: 'absolute',
                        right: 16,
                        top: 16,
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" mt={3} mb={3}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Stack component="form" noValidate spacing={2.0}>
                        <TextField variant="outlined" label="Мерчант" type="text" disabled defaultValue={activeRow?.merchant_name} fullWidth />

                        <TextField
                            variant="outlined"
                            label="Модель *"
                            select
                            SelectProps={{MenuProps: {className: 'select-menu'}}}
                            helperText={errorState.deskModel}
                            placeholder="Модель кассы"
                            inputRef={kkmTypeInputRef}
                            defaultValue={
                                !isCreate
                                    ? activeRow
                                        ? KKMType &&
                                          Object.keys(KKMType).find(itemId => {
                                              return KKMType[Number(itemId)].name === activeRow.model;
                                          })
                                        : ''
                                    : '0'
                            }
                            onFocus={() => setErrorState({...errorState, deskModel: ''})}
                            error={errorState.deskModel.length > 0}
                            fullWidth
                        >
                            <MenuItem value={0}>Не выбрано</MenuItem>
                            {KKMType &&
                                Object.keys(KKMType)
                                    .sort((a, b) => {
                                        const nameA = KKMType[Number(a)].name.toLowerCase();
                                        const nameB = KKMType[Number(b)].name.toLowerCase();
                                        return nameA.localeCompare(nameB);
                                    })
                                    .map(itemId => (
                                        <MenuItem key={itemId} value={itemId}>
                                            {KKMType[Number(itemId)].name}
                                        </MenuItem>
                                    ))}
                        </TextField>

                        <TextField
                            variant="outlined"
                            label="Cерийный номер"
                            type="text"
                            required
                            inputRef={serialNumberInputRef}
                            defaultValue={isCreate ? '' : (activeRow?.serial_number ?? '')}
                            helperText={errorState.serialNumber}
                            error={errorState.serialNumber.length > 0}
                            onFocus={() => setErrorState({...errorState, serialNumber: ''})}
                            disabled={!isCreate}
                            fullWidth
                        />

                        <TextField
                            variant="outlined"
                            label="Регистрационный номер"
                            type="number"
                            required
                            inputRef={regNumberInputRef}
                            defaultValue={isCreate ? '' : activeRow ? activeRow.reg_number : ''}
                            helperText={errorState.regNumber}
                            error={errorState.regNumber.length > 0}
                            onFocus={() => setErrorState({...errorState, regNumber: ''})}
                            fullWidth
                        />

                        <TextField
                            variant="outlined"
                            label="Место торговли"
                            select
                            SelectProps={{MenuProps: {className: 'select-menu'}}}
                            helperText={errorState.deskModel}
                            inputRef={departmentInputRef}
                            defaultValue={isCreate ? 0 : parseInt(activeRow?.department?.id, 10)}
                            fullWidth
                        >
                            <MenuItem value={0}>Не выбрано</MenuItem>
                            {isLoadingDepartments ? (
                                <Box display="flex" justifyContent="center" p={2}>
                                    <CircularProgress size={20} />
                                </Box>
                            ) : (
                                departments?.map(item => (
                                    <MenuItem key={item.id} value={item.id}>
                                        {item.name ? item.name : ''}
                                        {item.name && item.address ? ' / ' : ''}
                                        {item.address ? item.address : ''}
                                    </MenuItem>
                                ))
                            )}
                        </TextField>

                        <TextField
                            variant="outlined"
                            label="ОФД *"
                            placeholder="ОФД"
                            select
                            SelectProps={{MenuProps: {className: 'select-menu'}}}
                            helperText={errorState.ofd}
                            inputRef={ofdInputRef}
                            defaultValue={!isCreate ? (activeRow ? OFD && activeRow.ofd?.id : '') : '0'}
                            onFocus={() => setErrorState({...errorState, ofd: ''})}
                            error={errorState.ofd.length > 0}
                            onChange={e => setCurrentOfdId(Number(e.target.value))}
                            fullWidth
                        >
                            <MenuItem value={0}>Не выбрано</MenuItem>
                            {OFD &&
                                Object.keys(OFD)
                                    .sort((a, b) => {
                                        const nameA = OFD[Number(a)].toLowerCase();
                                        const nameB = OFD[Number(b)].toLowerCase();
                                        return nameA.localeCompare(nameB);
                                    })
                                    .map(itemId => (
                                        <MenuItem key={itemId} value={itemId}>
                                            {OFD[Number(itemId)]}
                                        </MenuItem>
                                    ))}
                        </TextField>

                        {isCreate ? renderRentInfoOrSwitches() : renderRentDetails()}

                        {!isCreate && !hasOnlyPremiumOFD && (
                            <Paper variant="outlined" sx={{p: 2, mt: 2, backgroundColor: '#f9f9f9'}}>
                                <FormControlLabel
                                    control={<Switch checked={disabledByClient} onChange={() => setDisabledByClient(prev => !prev)} />}
                                    label={
                                        <Box display="flex" alignItems="center">
                                            {disabledByClient ? 'Отменить приостановку услуги' : 'Приостановить услугу'}
                                            <HelpPopover
                                                content={
                                                    <>
                                                        <strong>«Приостановить услугу»</strong>
                                                        <br />
                                                        <br />
                                                        — На странице «Клиенты и кассы» рядом с кассой будет отображён соответствующий индикатор
                                                        <br />— При массовом добавлении касс в корзину оплаты, данная касса будет исключена из
                                                        оплаты.
                                                    </>
                                                }
                                            />
                                        </Box>
                                    }
                                />
                            </Paper>
                        )}

                        {isCreate ? (
                            <Box sx={{mt: 4, mb: 4}}>
                                <Button onClick={() => void handlerAdd()} sx={{mr: 2}} disabled={isSaving}>
                                    Добавить
                                </Button>
                                <Button variant="text" onClick={handlerClose} disabled={isSaving}>
                                    Отмена
                                </Button>
                            </Box>
                        ) : (
                            <Grid container justifyContent="space-between">
                                <Grid item>
                                    <Button onClick={() => void handlerUpdate()} sx={{mr: 2}} disabled={isSaving}>
                                        Сохранить
                                    </Button>
                                    <Button variant="text" onClick={handlerClose} disabled={isSaving}>
                                        Отмена
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Button variant="text" color="error" onClick={() => void handlerRemove()} disabled={isSaving}>
                                        Удалить
                                    </Button>
                                </Grid>
                            </Grid>
                        )}
                    </Stack>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default KKMPopup;
